import { create } from 'zustand';
import axios from '../utils/axios';
import { toast } from 'react-hot-toast';

const getInitialWishlist = () => {
  try {
    const token = localStorage.getItem('token');
    if (token) return [];
    const guestWishlist = localStorage.getItem('guest_wishlist');
    return guestWishlist ? JSON.parse(guestWishlist) : [];
  } catch (e) {
    return [];
  }
};

const useWishlistStore = create((set, get) => ({
    wishlist: getInitialWishlist(),
    loading: false,

    fetchWishlist: async () => {
        set({ loading: true });
        try {
            const useAuthStore = (await import('./useAuthStore')).default;
            const { isAuthenticated } = useAuthStore.getState();

            if (isAuthenticated) {
                const { data } = await axios.get('/wishlist');
                set({ wishlist: data.data?.products || [] });
            } else {
                const guestWishlist = localStorage.getItem('guest_wishlist');
                set({ wishlist: guestWishlist ? JSON.parse(guestWishlist) : [] });
            }
        } catch (error) {
            console.error('Wishlist fetch error:', error);
        } finally {
            set({ loading: false });
        }
    },

    addToWishlist: async (productId) => {
        try {
            const useAuthStore = (await import('./useAuthStore')).default;
            const { isAuthenticated } = useAuthStore.getState();

            if (isAuthenticated) {
                const { data } = await axios.post(`/wishlist/${productId}`);
                set({ wishlist: data.data.products });
                toast.success('Favorilere eklendi');
                return true;
            } else {
                set({ loading: true });
                const { data } = await axios.get(`/products/${productId}`);
                const product = data.data;

                let localWishlist = get().wishlist;
                const exists = localWishlist.some(item => 
                    (item.product?._id || item.product) === productId
                );

                if (exists) {
                    toast.success('Zaten favorilerinizde');
                    set({ loading: false });
                    return true;
                }

                const updatedWishlist = [...localWishlist, { 
                    product, 
                    addedAt: new Date().toISOString() 
                }];
                localStorage.setItem('guest_wishlist', JSON.stringify(updatedWishlist));
                set({ wishlist: updatedWishlist, loading: false });
                toast.success('Favorilere eklendi');
                return true;
            }
        } catch (error) {
            const message = error.response?.data?.message || 'Favorilere eklenemedi';
            if (error.response?.status === 400 && message.includes('zaten favorilerde')) {
                get().fetchWishlist();
                toast.success('Zaten favorilerinizde');
                return true;
            }
            toast.error(message);
            set({ loading: false });
            return false;
        }
    },

    removeFromWishlist: async (productId) => {
        try {
            const useAuthStore = (await import('./useAuthStore')).default;
            const { isAuthenticated } = useAuthStore.getState();

            if (isAuthenticated) {
                const { data } = await axios.delete(`/wishlist/${productId}`);
                set({ wishlist: data.data.products });
                toast.success('Favorilerden kaldırıldı');
                return true;
            } else {
                let localWishlist = get().wishlist;
                const updatedWishlist = localWishlist.filter(item => 
                    (item.product?._id || item.product) !== productId
                );
                localStorage.setItem('guest_wishlist', JSON.stringify(updatedWishlist));
                set({ wishlist: updatedWishlist });
                toast.success('Favorilerden kaldırıldı');
                return true;
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Favorilerden kaldırılamadı');
            return false;
        }
    },

    clearWishlist: async () => {
        try {
            const useAuthStore = (await import('./useAuthStore')).default;
            const { isAuthenticated } = useAuthStore.getState();

            if (isAuthenticated) {
                await axios.delete('/wishlist');
                set({ wishlist: [] });
                toast.success('Favori listesi temizlendi');
            } else {
                localStorage.removeItem('guest_wishlist');
                set({ wishlist: [] });
                toast.success('Favori listesi temizlendi');
            }
        } catch (error) {
            toast.error('Liste temizlenemedi');
        }
    },

    syncWishlist: async () => {
        const guestWishlistJson = localStorage.getItem('guest_wishlist');
        if (!guestWishlistJson) return;

        try {
            const guestWishlist = JSON.parse(guestWishlistJson);
            if (guestWishlist && guestWishlist.length > 0) {
                for (const item of guestWishlist) {
                    const productId = item.product?._id || item.product;
                    await axios.post(`/wishlist/${productId}`).catch(() => {});
                }
            }
            localStorage.removeItem('guest_wishlist');
            await get().fetchWishlist();
        } catch (error) {
            console.error('Failed to sync guest wishlist:', error);
        }
    },

    // Clear wishlist store locally (used on logout)
    clearWishlistStore: () => {
        set({ wishlist: [] });
    },

    isInWishlist: (productId) => {
        const { wishlist } = get();
        return wishlist.some(item =>
            (item.product?._id || item.product) === productId
        );
    }
}));

export default useWishlistStore;
